import React, { useState, useCallback, memo, useMemo } from 'react';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { chatInputAtom, voiceModeAtom } from '../../../../atoms/v3/chatInputAtoms';
import { messageOpacityAtom } from '../../../../atoms/v3/chatPersonalizationAtoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import {
  getPromptCategories,
  getRandomWelcomeMessage,
  getRandomSubMessage,
  type PromptCategory,
  type PromptItem
} from '../../../../data/newConversationPrompts';
import { RecentTasksSection } from './RecentTasksSection';

interface NewConversationPromptsProps {
  className?: string;
}

/**
 * 新对话启动提示组件
 * 模仿 ChatGPT 的二级交互设计
 * 只设置输入内容，不自动发送
 */
const NewConversationPromptsComp: React.FC<NewConversationPromptsProps> = ({
  className = ''
}) => {
  const { t } = useTranslation(['common', 'v3/chat']);
  const [selectedCategory, setSelectedCategory] = useState<PromptCategory | null>(null);
  const [welcomeMessage] = useState(() => getRandomWelcomeMessage(t));
  const [subMessage] = useState(() => getRandomSubMessage(t));
  const setInput = useSetAtom(chatInputAtom);
  const [voiceMode, setVoiceMode] = useAtom(voiceModeAtom);
  const messageOpacity = useAtomValue(messageOpacityAtom);
  const isDark = useAtomValue(isDarkAtom);

  // 获取国际化的提示分类数据
  const promptCategories = useMemo(() => getPromptCategories(t), [t]);

  // 计算功能卡片的背景样式（应用和AI消息一样的透明度设置）
  const getCardBackgroundStyle = useMemo(() => {
    const opacity = messageOpacity / 100; // 转换为0-1的透明度值

    if (isDark) {
      // 夜间模式：始终使用 iOS dark-gray4 颜色
      // #3A3A3C (58, 58, 60)
      return {
        backgroundColor: `rgba(58, 58, 60, ${opacity})`,
      } as React.CSSProperties;
    } else {
      // 日间模式：透明度100%时使用gray4，其他情况使用白色半透明
      if (opacity === 1) {
        // 100%透明度：使用 iOS gray4 颜色 #D1D1D6 (209, 209, 214)
        return {
          backgroundColor: `rgba(209, 209, 214, 1)`,
        } as React.CSSProperties;
      } else {
        // 非100%透明度：使用白色半透明
        return {
          backgroundColor: `rgba(255, 255, 255, ${opacity})`,
        } as React.CSSProperties;
      }
    }
  }, [messageOpacity, isDark]);

  // 计算功能卡片的文本颜色
  const getCardTextColor = useMemo(() => {
    return isDark ? 'text-white' : 'text-ios-black';
  }, [isDark]);

  // 处理分类选择
  const handleCategorySelect = useCallback((category: PromptCategory) => {
    setSelectedCategory(category);
  }, []);

  // 处理返回上一级
  const handleBack = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  // 处理提示选择 - 只设置输入，不发送
  const handlePromptSelect = useCallback((prompt: PromptItem) => {
    // 如果当前是语音模式，强制切换到文本模式
    if (voiceMode) {
      setVoiceMode(false);
    }
    setInput(prompt.text);
  }, [setInput, voiceMode, setVoiceMode]);

  // 渲染分类网格
  const renderCategories = () => (
    <div className="w-full max-w-2xl mx-auto">
      {/* Tempo Logo */}
      <div className="text-center mb-4">
        <img
          src="/icons/icon-512.webp"
          alt="Tempo"
          className="w-12 h-12 mx-auto rounded-lg"
        />
      </div>

      {/* 欢迎信息 */}
      <div className="text-center mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-medium text-ios-black dark:text-white mb-2">
          {welcomeMessage}
        </h1>
        <p className="text-sm md:text-base text-ios-gray dark:text-ios-dark-gray">
          {subMessage}
        </p>
      </div>

      {/* 分类网格 */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {promptCategories.map((category) => (
          <button
            key={category.id}
            onClick={() => handleCategorySelect(category)}
            className="group p-4 md:p-6 rounded-2xl border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-dark-blue hover:shadow-lg transition-all duration-200 text-left active:scale-95"
            style={getCardBackgroundStyle}
          >
            <div className="text-2xl md:text-3xl mb-2 md:mb-3">{category.icon}</div>
            <h3 className={`text-base md:text-lg font-medium ${getCardTextColor} mb-1`}>
              {category.title}
            </h3>
            <p className="text-xs md:text-sm text-ios-gray dark:text-ios-dark-gray">
              {category.description}
            </p>
          </button>
        ))}
      </div>

      {/* 最近要做的事情 */}
      <RecentTasksSection />
    </div>
  );

  // 渲染具体提示列表
  const renderPrompts = () => {
    if (!selectedCategory) return null;

    return (
      <div className="w-full max-w-2xl mx-auto">
        {/* 返回按钮和标题 */}
        <div className="flex items-center mb-6">
          <button
            onClick={handleBack}
            className="flex items-center text-ios-blue dark:text-ios-dark-blue hover:opacity-70 transition-opacity mr-4"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {t('common:actions.back', '返回')}
          </button>
          <div className="flex items-center">
            <span className="text-2xl mr-2">{selectedCategory.icon}</span>
            <h2 className="text-xl font-medium text-ios-black dark:text-white">
              {selectedCategory.title}
            </h2>
          </div>
        </div>

        {/* 提示列表 */}
        <div className="space-y-3">
          {selectedCategory.prompts.map((prompt) => (
            <button
              key={prompt.id}
              onClick={() => handlePromptSelect(prompt)}
              className="w-full p-4 rounded-xl border border-ios-gray5 dark:border-ios-dark-gray4 hover:border-ios-blue dark:hover:border-ios-dark-blue hover:shadow-md transition-all duration-200 text-left group active:scale-[0.98]"
              style={getCardBackgroundStyle}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className={`${getCardTextColor} font-medium mb-1`}>
                    {prompt.text}
                  </p>
                  {prompt.description && (
                    <p className="text-sm text-ios-gray dark:text-ios-dark-gray">
                      {prompt.description}
                    </p>
                  )}
                </div>
                <svg
                  className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray group-hover:text-ios-blue dark:group-hover:text-ios-dark-blue transition-colors ml-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>

        {/* 底部提示 */}
        <div className="text-center mt-6">
          <p className="text-sm text-ios-gray dark:text-ios-dark-gray">
            {t('v3/chat:prompts.instruction', '选择一个提示，或者在下方输入框中自定义你的问题')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className={`flex-1 overflow-y-auto pt-[calc(4.5rem+env(safe-area-inset-top))] pb-[calc(10rem+env(safe-area-inset-bottom))] ${className}`}>
      <div className="min-h-full flex items-center justify-center px-4 py-8">
        {selectedCategory ? renderPrompts() : renderCategories()}
      </div>
    </div>
  );
};

export const NewConversationPrompts = memo(NewConversationPromptsComp);
export default NewConversationPrompts;
